import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { cn } from "@/lib/utils.ts";
import { useSbCalls } from "@/lib/sendbird-calls";
import { Label } from "@/components/ui/label.tsx";
import { useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { DeviceTester } from "./device-tester";

interface DeviceSettingsProps {
  open: boolean;
  onClose: () => void;
}

export function DeviceSettings({ open, onClose }: DeviceSettingsProps) {
  const sbCalls = useSbCalls();

  // --- Mic & Camera Tester State ---
  const [isTesterOpen, setIsTesterOpen] = useState(false);

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!newOpen) {
          onClose();
        }
      }}
    >
      <DialogContent className="w-full max-w-lg p-0">
        <div className="p-4">
          <DialogHeader>
            <DialogTitle className="mb-1 text-2xl font-semibold">
              Device Settings
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 flex flex-col gap-4">
            <div className="flex flex-row items-center gap-8">
              <Label className="w-1/2">Camera</Label>
              <Select
                value={sbCalls.videoInputDeviceInfo.current?.deviceId}
                onValueChange={(value) => {
                  const mediaInfo = sbCalls.videoInputDeviceInfo.available.find(
                    (device) => device.deviceId === value,
                  );
                  if (mediaInfo) sbCalls.selectVideoInputDevice(mediaInfo);
                }}
              >
                <SelectTrigger className={cn("w-full")}>
                  <SelectValue placeholder="Select camera" />
                </SelectTrigger>
                <SelectContent>
                  {sbCalls.videoInputDeviceInfo.available.map((info) => {
                    if (!info.deviceId) return null;

                    return (
                      <SelectItem key={info.deviceId} value={info.deviceId}>
                        {info.label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-row items-center gap-8">
              <Label className="w-1/2">Microphone</Label>
              <Select
                value={sbCalls.audioInputDeviceInfo.current?.deviceId}
                onValueChange={(value) => {
                  const mediaInfo = sbCalls.audioInputDeviceInfo.available.find(
                    (device) => device.deviceId === value,
                  );
                  if (mediaInfo) sbCalls.selectAudioInputDevice(mediaInfo);
                }}
              >
                <SelectTrigger className={cn("w-full")}>
                  <SelectValue placeholder="Select microphone" />
                </SelectTrigger>
                <SelectContent>
                  {sbCalls.audioInputDeviceInfo.available.map((info) => {
                    if (!info.deviceId) return null;
                    return (
                      <SelectItem key={info.deviceId} value={info.deviceId}>
                        {info.label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-row items-center gap-8">
              <Label className="w-1/2">Speaker</Label>
              <Select
                value={sbCalls.audioOutputDeviceInfo.current?.deviceId}
                onValueChange={(value) => {
                  const mediaInfo =
                    sbCalls.audioOutputDeviceInfo.available.find(
                      (device) => device.deviceId === value,
                    );
                  if (mediaInfo) sbCalls.selectAudioOutputDevice(mediaInfo);
                }}
              >
                <SelectTrigger className={cn("w-full")}>
                  <SelectValue placeholder="Select speaker" />
                </SelectTrigger>
                <SelectContent>
                  {sbCalls.audioOutputDeviceInfo.available.map((info) => {
                    if (!info.deviceId) return null;
                    return (
                      <SelectItem key={info.deviceId} value={info.deviceId}>
                        {info.label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Accordion
            type="single"
            collapsible
            className="mt-4 w-full"
            onValueChange={(value) => setIsTesterOpen(value === "tester")}
          >
            <AccordionItem value="tester" className="border-none">
              <AccordionTrigger>
                <span className="text-base font-semibold">
                  Test your mic and camera
                </span>
              </AccordionTrigger>
              <AccordionContent>
                {isTesterOpen && (
                  <DeviceTester sbCalls={sbCalls} isOpen={isTesterOpen} />
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </DialogContent>
    </Dialog>
  );
}
